import React, { memo, useCallback, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Input, SpinnerOverlay, SubmissionButtons } from '@components/common';

import useMutateNetworkElementsCheckPath from '@hooks/networkElements/useMutateNetworkElementsCheckPath';
import useMutateNetworkElementsGetVesPathCu from '@hooks/networkElements/useMutateNetworkElementsGetVesPathCu';
import useMutateNetworkElementsGetVesPathDu from '@hooks/networkElements/useMutateNetworkElementsGetVesPathDu';
import useQueryNetworkElementCheckVesCu from '@hooks/networkElements/useQueryNetworkElementCheckVesCu';
import useQueryNetworkElementCheckVesDu from '@hooks/networkElements/useQueryNetworkElementCheckVesDu';

import { isNotEmpty } from '@utils/common';

import { INetworkElementsSFTPPathsDTO } from '@interfaces/networkElements/dto/networkElements';
import { INetworkElement } from '@interfaces/networkElements/networkElements';

import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { DEFAULT_SFTP_PATH } from '../constants';

import { pathsSchema } from './schema';
import { createVesPathGetter, determineElementPathFlags } from './utils';

import styles from './NetworkElementsSFTPForm.module.scss';

interface INetworkElementsSTFPForm {
	onSubmit: (dto: INetworkElementsSFTPPathsDTO) => void;
	onClose: () => void;
	setIsDirtyForm: (isDirty: boolean) => void;
	networkElement?: INetworkElement;
	id: string;
	isLoadingSubmit?: boolean;
}

const NetworkElementsSFTPForm = ({
	onSubmit,
	onClose,
	setIsDirtyForm,
	networkElement,
	isLoadingSubmit,
	id,
}: INetworkElementsSTFPForm) => {
	const { isCuPath, isDuPath } = determineElementPathFlags(networkElement?.type.name);

	const { handleCheckPath } = useMutateNetworkElementsCheckPath();

	const schema = pathsSchema({ handleCheckPath, id, isCuPath, isDuPath });

	const {
		formState: { errors, isDirty },
		handleSubmit,
		register,
		setValue,
		trigger,
		watch,
	} = useForm<FieldValues>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(schema),
		defaultValues: {
			isCuPath,
			isDuPath,
			pathCu: networkElement?.pathCu || DEFAULT_SFTP_PATH,
			pathDu: networkElement?.pathDu || DEFAULT_SFTP_PATH,
		},
	});

	const { getPathCu } = useMutateNetworkElementsGetVesPathCu();
	const { getPathDu } = useMutateNetworkElementsGetVesPathDu();
	const { isAvailableVesCu, isLoadingCheckVesCu } = useQueryNetworkElementCheckVesCu(id);
	const { isAvailableVesDu, isLoadingCheckVesDu } = useQueryNetworkElementCheckVesDu(id);

	const pathCu = watch('pathCu');
	const pathDu = watch('pathDu');

	const isSubmitDisabled = isNotEmpty(errors);
	const isLoading = isLoadingSubmit
		? false
		: isLoadingCheckVesCu || isLoadingCheckVesDu;

	useEffect(() => {
		setIsDirtyForm(isDirty);
	}, [isDirty, setIsDirtyForm]);

	const onGetPathCu = useCallback(() => createVesPathGetter(getPathCu, setValue, id, 'pathCu', trigger), [trigger, getPathCu, setValue, id]);
	const onGetPathDu = useCallback(() => createVesPathGetter(getPathDu, setValue, id, 'pathDu', trigger), [trigger, getPathDu, setValue, id]);

	const submitHandler = useCallback(() => {
		onSubmit({
			pathCu: isCuPath ? pathCu : undefined,
			pathDu: isDuPath ? pathDu : undefined,
			id,
		});
	}, [
		onSubmit,
		pathCu,
		pathDu,
		isCuPath,
		isDuPath,
		id,
	]);

	return (
		<div className={styles.container}>
			{isLoading && <SpinnerOverlay />}
			{isCuPath && (
				<Input
					errorMessage={errors?.pathCu?.message}
					label='SFTP Server Path (CU)'
					labelAction={onGetPathCu}
					labelActionDisabled={!isAvailableVesCu}
					labelActionText='Get path from VES'
					labelActionTooltip='Get path from the recent VES event'
					name='pathCu'
					placeholder='Enter SFTP server path (CU)'
					register={register}
					required
				/>
			)}
			{isDuPath && (
				<Input
					errorMessage={errors?.pathDu?.message}
					label='SFTP Server Path (DU)'
					labelAction={onGetPathDu}
					labelActionDisabled={!isAvailableVesDu}
					labelActionText='Get path from VES'
					labelActionTooltip='Get path from the recent VES event'
					name='pathDu'
					placeholder='Enter SFTP server path (DU)'
					register={register}
					required
				/>
			)}
			<SubmissionButtons
				disabledPositive={isSubmitDisabled}
				negativeHandler={onClose}
				positiveHandler={handleSubmit(submitHandler)}
				positiveLabel='Proceed'
			/>
		</div>
	);
};

export default memo(NetworkElementsSFTPForm);
